<template>
  <v-app>
    <v-main>
      <v-row no-gutters>
        <v-col
          :cols="viewerCols"
          :style="viewerColStyle"
        >
          <viewer
            @arClick="arAction()"
            @resetClick="resetAction()"
            @dimensionsClick="dimensionsAction()"
            @screenshotClick="screenshotAction()"
            @projectClick="projectAction()"
          />
        </v-col>
        <v-col
          v-if="isOptionsPanelDisplayed"
          :cols="optionsPanelCols"
          :style="optionsPanelColStyle"
        >
          <options-panel
            :options="options"
            @optionsItemDidSelected="loadOptionsItem"
          />
        </v-col>
      </v-row>
      <project-code
        v-model="isProjectCodeDisplayed"
        :code="projectCode"
      />
      <qrcode-ar
        v-model="isQrcodeArDisplayed"
        :url="qrcodeArUrl"
      />
    </v-main>
  </v-app>
</template>

<script>
  import scheme from './scheme/scheme.js'
  import transmit from './js/transmit.js'
  import { mapGetters, mapActions } from 'vuex'
  import Viewer from './components/viewer/Viewer.vue'
  import OptionsPanel from './components/options/OptionsPanel.vue'
  import ProjectCode from './components/project/ProjectCode.vue'
  import QrcodeAr from './components/qrcode/QrcodeAr.vue'
  import helpers from './js/helpers.js'

  export default {
    name: 'App',

    components: {
      Viewer,
      ProjectCode,
      QrcodeAr,
      OptionsPanel,
    },

    data () {
      return {
        // inspi-viewer component
        inspiViewer: null,

        // Options
        collectionOptions: null,
        configurationOptions: null,

        // Project
        isProjectCodeDisplayed: false,
        projectCode: null,

        // AR
        isQrcodeArDisplayed: false,
        qrcodeArUrl: null,
      }
    },

    computed: {
      ...mapGetters([
        'isMenuDisplayed',
        'isDividersDisplayed',
        'isCollectionDisplayed',
        'isProductLoaded',
        'isFrontScreenshotEnabled',
        'isProjectButtonDisplayed',
        'isProjectCreating',
        'isArLoading']),
      viewerCols () {
        if (!this.isOptionsPanelDisplayed || this.$vuetify.breakpoint.xsOnly) return '12'
        return 12 - this.optionsPanelCols
      },
      optionsPanelCols () {
        if (this.$vuetify.breakpoint.xsOnly) return '12'
        else if (this.$vuetify.breakpoint.smOnly) return '5'
        else if (this.$vuetify.breakpoint.mdOnly) return '4'
        return '3'
      },
      viewerColStyle () {
        return (this.$vuetify.breakpoint.xsOnly ? 'height:60vh;' : 'height:100vh;')
      },
      optionsPanelColStyle () {
        if (!this.isDividersDisplayed) return 'height:100vh;'
        else if (this.$vuetify.breakpoint.xsOnly) return 'height:40vh; border-top: solid lightgray 0.08rem;'
        else return 'height:100vh; border-left: solid lightgray 0.08rem;'
      },
      isOptionsPanelDisplayed () {
        return this.isMenuDisplayed && this.isProductLoaded && this.options != null && this.options.length > 0
      },
      options () {
        if (this.collectionOptions != null && this.configurationOptions != null) return this.collectionOptions.concat(this.configurationOptions)
        else if (this.collectionOptions != null) return this.collectionOptions
        else if (this.configurationOptions != null) return this.configurationOptions
        else return null
      },
    },

    created () {
      console.log(scheme.title + ' version: ' + require('../package.json').version)
      this.setArAvailable(true)
    },

    mounted () {
      document.onreadystatechange = () => {
        if (document.readyState === 'complete') {
          this.attachViewerListeners(document.querySelector('#viewer-container'))
          this.attachParentListeners()
        }
      }
    },

    methods: {
      ...mapActions([
        'setProductLoaded',
        'setProductName',
        'setProductPrice',
        'setArButtonDisplayed',
        'setProjectCreating',
        'setArLoading',
      ]),

      attachParentListeners () {
        window.addEventListener('message', event => {
          event.stopPropagation()

          switch (event.data.type) {
            case 'inspiGetSummary':
              transmit.postSummary(this.inspiViewer.getConfigurationSummary())
              break

            case 'inspiGetConfiguration':
              transmit.postConfiguration(this.inspiViewer.getConfigurationCode())
              break

            case 'inspiGetProject':
              this.projectAction()
              break
          }
        })
      },

      attachViewerListeners (viewer) {
        // An error occurred
        viewer.addEventListener('badRequest', event => {
          event.stopPropagation()
          console.log(event.detail.message)
          if (this.isArLoading) this.setArLoading(false)
          if (this.isProjectCreating) this.setProjectCreating(false)
          transmit.postError(event.detail.message)
        })

        // The viewer has been loaded
        viewer.addEventListener('componentLoaded', event => {
          event.stopPropagation()
          this.inspiViewer = window.INSPI
          transmit.postComponentLoaded()
        })

        // The product has been loaded
        viewer.addEventListener('INSPIFurnitureLoaded', event => {
          event.stopPropagation()
          this.setProductLoaded(true)
          this.updateProductNameAndPrice()
          transmit.postProductLoaded()
        })

        // Available collection have changed
        viewer.addEventListener('APICollection', event => {
          event.stopPropagation()
          this.buildCollection(event.detail.collectionObject)
        })

        // Available configurations have changed
        viewer.addEventListener('APIFullConfig', event => {
          event.stopPropagation()
          this.updateProductNameAndPrice()
          this.configurationOptions = event.detail.fullconfigObject
        })

        // Project Code did generated successfully
        viewer.addEventListener('projectCode', event => {
          event.stopPropagation()
          if (this.isProjectButtonDisplayed) {
            this.setProjectCreating(false)
            this.displayProjectCode(event.detail.projectCode)
          }
          transmit.postProject(event.detail.projectCode)
        })

        // Pregenerated AR files are available
        viewer.addEventListener('isStaticAR', event => {
          event.stopPropagation()
          if (!scheme.isDynamicArAvailable && !event.detail.value) this.setArAvailable(false)
        })

        // AR QR Code did generated successfully
        viewer.addEventListener('externalAROpening', event => {
          event.stopPropagation()
          this.setArLoading(false)
          this.displayQrcodeAr(event.detail.urlQR)
        })

        // AR did launch successfully
        viewer.addEventListener('successAR', event => {
          event.stopPropagation()
          this.setArLoading(false)
        })
      },

      buildCollection (collection) {
        this.collectionOptions = null

        if (this.isCollectionDisplayed && collection != null && collection !== undefined && collection.length > 1) {
          const elements = []
          let selectedElement = null

          collection.forEach(element => {
            const shapedElement = { type: 'product', image: element.image, name: element.name, reference: element.loading_reference, is_selected: element.is_current_product }
            if (element.is_current_product) selectedElement = shapedElement
            elements.push(shapedElement)
          })

          this.collectionOptions = [{ type: 'collection', name: this.localize('collectionTitle'), elements: elements, selected_element: selectedElement }]
        }
      },

      updateProductNameAndPrice () {
        this.updateProductName()
        this.updateProductPrice()
      },

      updateProductName () {
        const name = this.inspiViewer.getName()
        if (name) {
          this.setProductName(name)
          transmit.postName(name)
        }
      },

      updateProductPrice () {
        const price = this.inspiViewer.getPrice()
        if (price) {
          this.setProductPrice(price.priceAsString)
          transmit.postPrice(price.priceAsString)
        }
      },

      resetAction () { this.inspiViewer.resetView() },
      dimensionsAction () { this.inspiViewer.activateDimensions() },
      screenshotAction () { this.isFrontScreenshotEnabled ? this.inspiViewer.getDataURL() : this.inspiViewer.getCurrentDataURL() },
      loadOptionsItem (element) {
        if (element.is_selected) return
        if (element.type === 'product') this.inspiViewer.loadModel({ ref: element.reference, reset_product: true })
        else this.inspiViewer.selectConfigurationAPI(element)
      },

      projectAction () {
        if (scheme.isProjectAvailable) {
          this.setProjectCreating(true)
          this.inspiViewer.getProjectCode()
        } else {
          console.log('Project creation are not available')
        }
      },

      displayProjectCode (code) {
        if (scheme.isProjectAvailable) {
          this.projectCode = code
          this.isProjectCodeDisplayed = true
        }
      },

      setArAvailable (isAvailable) {
        if (helpers.isDesktop() && !scheme.isQrcodeArAvailable) { isAvailable = false }
        this.setArButtonDisplayed(isAvailable)
      },

      arAction () {
        this.setArLoading(true)
        scheme.isDynamicArAvailable ? this.inspiViewer.launchAR() : this.inspiViewer.launchStaticAR()
      },

      displayQrcodeAr (url) {
        if (scheme.isQrcodeArAvailable) {
          this.qrcodeArUrl = url
          this.isQrcodeArDisplayed = true
        }
      },
    },
  }
</script>
